import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "store/index";
import {
  deleteDepositPointLimit,
  getDepositPointLimits,
  handleClear,
  handlePaginate,
  setDepositPointLimitPeriod,
} from "store/liveOps/loyalty/depositPointLimits/slice";
import { loyaltyListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import {
  NotificationTypes,
  showNotification,
} from "store/notifications/slice";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";
import { handleApiError } from "helpers/handleApiError";

import Pagination from "components/Pagination";
import ActionBtn from "components/buttons/ActionBtn";
import { RefreshButton } from "components/buttons/RefreshButton";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Filter from "components/global/Filter/Filter";
import Tooltip from "components/Tooltip";
import { FilterItemTypes } from "configs/types";
import gridIcon from "assets/icons/grid-icon.svg";
import styles from "./DepositPointsLimitsList.module.scss";

const PERIODS = ["DAY", "WEEK", "MONTH"] as const;

const DepositPointsLimitsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermissionHelper();
  const canRead = permissions?.read ?? false;
  const canCreate = permissions?.create ?? false;
  const canUpdate = permissions?.update ?? false;
  const canDelete = permissions?.delete ?? false;

  const [currentLimit, setLimit] = useState<number>(loyaltyListLimits[0]);
  const [title, setTitle] = useState("");
  const [level, setLevel] = useState("");
  const [appliedTitle, setAppliedTitle] = useState("");
  const [appliedLevel, setAppliedLevel] = useState("");

  const list = useAppSelector((store) => store["deposit-point-limits"].list);
  const isListFetched = useAppSelector(
    (store) => store["deposit-point-limits"].isListFetched,
  );
  const totalItems = useAppSelector(
    (store) => store["deposit-point-limits"].totalItems,
  );
  const totalPages = useAppSelector(
    (store) => store["deposit-point-limits"].totalPages,
  );
  const activePage = useAppSelector(
    (store) => store["deposit-point-limits"].activePage,
  );
  const period = useAppSelector((store) => store["deposit-point-limits"].period);
  const processingItemId = useAppSelector(
    (store) => store["deposit-point-limits"].processingItemId,
  );

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: "deposit-point-limits",
    isListFetched,
  });

  useEffect(() => {
    return () => {
      dispatch(handleClear());
    };
  }, [dispatch]);

  useEffect(() => {
    if (canRead && !isListFetched) {
      dispatch(
        getDepositPointLimits({
          limit: currentLimit,
          offset: activePage - 1,
          title: appliedTitle || undefined,
          level: appliedLevel || undefined,
        }),
      );
    }
  }, [
    activePage,
    appliedLevel,
    appliedTitle,
    canRead,
    currentLimit,
    dispatch,
    isListFetched,
  ]);

  const handleOpenEditModal = (id: number) => {
    if (!canRead) return;
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.depositPointLimit}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.depositPointLimit }));
  };

  const handleOpenCreateModal = () => {
    if (!canCreate) return;
    navigate(`${location.pathname}?activeModal=${ModalTypes.depositPointLimit}`);
    dispatch(openModal({ activeModal: ModalTypes.depositPointLimit }));
  };

  const handleDeleteItem = async (id: number) => {
    if (!canDelete) return;
    const result = await dispatch(deleteDepositPointLimit(id));
    if (result?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const handlePeriod = async (value: string | number) => {
    if (!canUpdate) return;
    const next = String(value);
    if (!PERIODS.includes(next as (typeof PERIODS)[number]) || next === period) return;
    try {
      await dispatch(setDepositPointLimitPeriod(next)).unwrap();
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityUpdateSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } catch (error) {
      handleApiError({ error, t, dispatch });
    }
  };

  if (!canRead) return null;

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <Filter
        handleClear={() => {
          setTitle("");
          setLevel("");
          setAppliedTitle("");
          setAppliedLevel("");
          dispatch(handlePaginate(1));
        }}
        handleApply={() => {
          setAppliedTitle(title);
          setAppliedLevel(level);
          dispatch(handlePaginate(1));
        }}
        filterItems={[
          {
            type: FilterItemTypes.input,
            title: t("title"),
            boxTitle: t("title"),
            icon: gridIcon,
            inputValue: title,
            setInputValue: setTitle,
          },
          {
            type: FilterItemTypes.input,
            title: t("level"),
            boxTitle: t("level"),
            icon: gridIcon,
            inputValue: level,
            inputType: "number",
            setInputValue: setLevel,
          },
        ]}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {canCreate && (
              <ActionBtn txt={t("add")} handleClick={handleOpenCreateModal} />
            )}
            <div className={styles.period}>
              <DefaultDropdown
                disabled={!canUpdate}
                value={period}
                placeholder={t("selectPeriod")}
                options={PERIODS.map((item) => ({
                  value: item,
                  label: t(item.toLowerCase()),
                }))}
                onSelect={handlePeriod}
              />
              <Tooltip
                text={`${t("depositPointsLimitPeriodHint")} ${t("depositPointsLimitsUncappedHint")}`}
                position="bottom"
                asChild
              >
                <button
                  type="button"
                  className="info-icon"
                  aria-label={`${t("globalPeriod")}: ${t("depositPointsLimitPeriodHint")} ${t("depositPointsLimitsUncappedHint")}`}
                >
                  ℹ
                </button>
              </Tooltip>
            </div>
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
        </div>
        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={6} rowsCount={7} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <>
            <div className="game-table-wrap">
              {list.length === 0 && (
                <EmptyState message={t("noDepositPointLimitsFound")} />
              )}
              {list.length > 0 && (
                <Table
                  data={list}
                  columns={{
                    hiddenColumns: ["loyaltyLevelId", "pointsLimit", "period"],
                    columnOrder: ["id", "level", "title", "depositPointsLimit", "updatedAt"],
                    sortingColumns: {
                      level: { value: t("level") },
                      title: { value: t("title") },
                      depositPointsLimit: { value: t("depositPointsLimit") },
                      updatedAt: { value: t("updatedAt") },
                    },
                  }}
                  idField="id"
                  onDelete={(id) => handleDeleteItem(id)}
                  onEdit={canUpdate ? (id) => handleOpenEditModal(id) : undefined}
                  onView={
                    canRead && !canUpdate
                      ? (id) => handleOpenEditModal(id)
                      : undefined
                  }
                  hasCheckbox={false}
                  hasEdit={canUpdate}
                  hasView={canRead && !canUpdate}
                  hasDelete={canDelete}
                  hasOptions={canRead || canUpdate || canDelete}
                  processingId={processingItemId}
                />
              )}
            </div>
            {list.length > 0 && (
              <Pagination
                handleLimitClick={(nextLimit) => {
                  setLimit(nextLimit);
                  dispatch(handlePaginate(1));
                }}
                limitsArray={loyaltyListLimits}
                activePage={activePage}
                limit={currentLimit}
                totalItems={totalItems}
                totalPages={totalPages}
                handleNextClick={() => {
                  if (activePage !== totalPages) dispatch(handlePaginate(activePage + 1));
                }}
                handlePrevClick={() => {
                  if (activePage > 1) dispatch(handlePaginate(activePage - 1));
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DepositPointsLimitsList;
